import React, { ForwardRefExoticComponent, RefAttributes, useContext, useEffect, useState } from "react";
import { Box, createStyles, makeStyles, Theme } from "@material-ui/core";
import { Form, Formik, FormikHelpers, FormikProps } from "formik";
import * as Yup from "yup";
import Grid from "@material-ui/core/Grid/Grid";
import PlaylistAdd from "@material-ui/icons/PlaylistAdd";
import { MaterialUiPickersDate } from "@material-ui/pickers/typings/date";
import {
  containsHighlyConfidentialInfo,
  fetchHighlyConfidentialRegex,
} from "../../../functions/confidentialCheck.function";
import CustomDialog from "../../../components/Dialog/CustomDialog";
import CustomTextField from "../../../components/Input/CustomTextField";
import CustomRadioGroup from "../../../components/Input/CustomRadioGroup";
import CustomDatePicker from "../../../components/Input/CustomDatePicker";
import SubstrateCharacteristicsTable from "./SubstrateCharacteristicsTable";
import CustomButton from "../../../components/Button/CustomButton";
import { tesaDarkRedError, tesaGreenPositive } from "../../../styles/colors";
import SubstrateSampleForm from "../../../api/models/substrate/SubstrateSampleForm";
import { SubstrateSampleContext } from "../../../contexts/SubstrateSampleContext";
import SubstrateSamplesApi from "../../../api/services/SubstrateSamplesApi";
import TestRequestApi from "../../../api/services/TestRequestApi";
import SubstrateSample from "../../../api/models/substrate/SubstrateSample";
import { toParameterValue } from "../../../validator/ParameterValueValidator";
import ParameterValue from "../../../api/models/test-series/ParameterValue";
import CustomDynamicContextMenu from "../../../components/Menu/CustomDynamicContextMenu";
import TesaTmbApi, { mapParameter } from "../../../api/services/TesaTmbApi";
import TmbParameter from "../../../api/models/tmb/TmbParameter";
import { labelOfSubstrateClassSelection } from "../../../api/models/substrate/SubstrateClassSelection";
import { labelOfReference } from "../../../api/models/substrate/SubstrateClassReference";
import sortParameterValue from "../../../helper/ParameterValueSorter";
import SubstrateSampleGallery from "./SubstrateSampleGallery";
import GalleryUploadButton from "../../../components/Gallery/button/GalleryUploadButton";
import SubstrateAttachment from "../../../api/models/substrate/SubstrateAttachment";
import GalleryButton from "../../../components/Gallery/button/GalleryButton";
import ConfirmationDialogContext from "../../../components/Dialog/ConfirmationDialog/ConfirmationDialogContext";
import ErrorSnackbarContext from "../../../contexts/ErrorSnackbarContext";
import TestRequestPermissionContext from "../../../contexts/TestRequestPermissionContext";
import useEnv from "../../../hooks/env";
import CustomSearchAutoComplete, {
  CustomSearchAutocompleteProps,
} from "../../../components/Autocomplete/CustomSearchAutoComplete";
import PagedResponse from "../../../api/models/PagedResponse";
import { CustomerPart } from "../../../api/models/substrate/CustomerPart";
import { SampleType, WithPredefinedChip } from "../../../components/Chip/PredefinedSampleChip";
import InfoBox from "../../../components/InfoBox";
import HiddenError from "../../../models/HiddenError";
import CustomerPartCreateDialog from "./CustomerPartCreateDialog";
import ConfidentialConfirmationDialog from "../../../components/Dialog/ConfidentialConfirmationDialog";

type DialogWithCallback = "detail" | "reuse" | "duplicate";

const INFO_CHARACTERISTICS =
  "Characteristics involve the physical properties of the sample. " +
  "*The list depends on the choice of substrate class." +
  "*When you have such information, enter it here – you can do it at any time." +
  "*Enter as much information as you can." +
  "*The information is essential for the quality of the data set which can be used for analylsis.";
const INFO_DELIVERED_BY = "Indicate who is responsible for delivering the sample to the appropriate testing lab.";
const INFO_DESCRIPTION =
  "Enter any available additional descriptions of the Substrate Sample (e.g. the type of Customer Part).";
const INFO_EDIT_CHARACTERISTICS_GALLERY =
  "Use characteristics to define all information beyond the exact material. " +
  "*For example, if you have a PC-GF50 sample, choose 'PC: Polycarbonate' as a substrate class and then enter all " +
  "the information in the characteristic dialog ('type: glass fiber', 'amount: 50%'). " +
  "*Use the upload section for any files related to the specific substrate sample (IR, TDS or picture of the sample " +
  "you want to use for the report).";
const INFO_SAMPLE_NAME =
  "Automatically pre-filled with the material you have chosen. " +
  "*You can change the sample name here as you see fit. " +
  "*Choose a short but meaningful name. " +
  "*In case of a customer part you should enter the type of customer part here.";
const INFO_SOURCE = "Indicate the customer or manufacturer.";

const CustomerPartSearchAutocomplete = CustomSearchAutoComplete as ForwardRefExoticComponent<
  CustomSearchAutocompleteProps<CustomerPart> & RefAttributes<{ reset: () => void }>
>;

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    header: {
      marginBottom: theme.spacing(5),
      marginTop: theme.spacing(4),
    },
    marginTop: {
      marginTop: theme.spacing(3),
    },
    successMessage: { color: tesaGreenPositive },
    errorMessage: { color: tesaDarkRedError },
  }),
);

interface SubstrateDetailsDialogProps {
  disabled?: boolean;
  open: boolean;
  testRequestId?: string;
  callback: (closeDialog: boolean, dialogWithCallback?: DialogWithCallback) => void;
  onClose: () => void;
}

const SubstrateDetailsDialog: React.FC<SubstrateDetailsDialogProps> = (props) => {
  const [showConfidentialDialog, setShowConfidentialDialog] = useState(false);
  const [pendingValues, setPendingValues] = useState<SubstrateSampleForm | null>(null);
  const [confidentialRegex, setConfidentialRegex] = useState<RegExp | null>(null);
  const { callback, disabled, open, onClose, testRequestId: requestIdOverride } = props;
  const [isCustomerPartDialogOpen, setIsCustomerPartDialogOpen] = useState(false);
  const classes = useStyles(props);
  const { api } = useEnv();
  const dialogContext = useContext(ConfirmationDialogContext);
  const tmbApi = new TesaTmbApi(dialogContext);
  const substrateSamplesApi = new SubstrateSamplesApi(api);
  const testRequestApi = new TestRequestApi(api);
  const {
    testRequest,
    substrateClasses,
    substrateContext,
    substrateId,
    updateSubstrateId,
    substrateObject,
    updateSubstrateObject,
    showParameterListDialog,
  } = useContext(SubstrateSampleContext);
  const { showConfirmationDialog } = dialogContext;
  const { showSnackbar, handleHttpError } = useContext(ErrorSnackbarContext);
  const { substrateSamples, newSubstrateSample } = useContext(TestRequestPermissionContext);

  useEffect(() => {
    fetchHighlyConfidentialRegex().then(setConfidentialRegex);
  }, []);
  const permissions = substrateId ? substrateSamples?.[substrateId] : newSubstrateSample;
  const formFieldsDisabled = disabled || !permissions?.editDefinition;

  let formRef: FormikProps<SubstrateSampleForm> | null = null;

  const [parameterValues, setParameterValues] = React.useState<ParameterValue[]>([]);
  const [substrateSampleName, setSubstrateSampleName] = useState("");
  const isCustomerPart =
    substrateObject?.substrateType === "CUSTOMER_PART" || substrateContext.includes("CUSTOMER_PART");

  useEffect(() => {
    if (substrateObject) {
      const characteristics = [...substrateObject.characteristics];
      characteristics.sort(sortParameterValue);
      setParameterValues(characteristics);
      setSubstrateSampleName(substrateObject.sampleName);
    } else if (substrateClasses.length > 0) {
      const characteristics = [...substrateClasses[0].substrateClass.parameters.map(toParameterValue)];
      characteristics.sort(sortParameterValue);
      setParameterValues(characteristics);
      setSubstrateSampleName(
        substrateClasses[0].kind === "standard"
          ? substrateClasses[0].substrateClass.name
          : substrateClasses[0].substrateClass.productName || "",
      );
    }
  }, [substrateObject, substrateClasses]);

  const yupValidationSchema = Yup.object().shape({
    customerPart: isCustomerPart
      ? Yup.object()
          .shape({ id: Yup.string().nullable(), name: Yup.string().required() })
          .nullable()
          .required("Please select an option or enter a new name")
      : Yup.object().nullable(),
    deliveredBy: Yup.string().required("Please select an option"),
    description: Yup.string(),
    externalId: Yup.string(),
    lotNumber: Yup.string(),
    manufacturer: Yup.string(),
    receivedOn: Yup.date().nullable(),
    sampleName: Yup.string()
      .required("Please fill out this field")
      .test(
        "invalid character test",
        "Name cannot contain a hash '#' symbol",
        (value) => value && !value.includes("#"),
      ),
    source: Yup.string(),
    usage: Yup.string().required("Please select an option"),
  });

  const save = (values: SubstrateSampleForm): Promise<SubstrateSample> => {
    const payload: SubstrateSample = {
      customerPartTypeId: values.customerPart?.id || null,
      customerPartTypeName: values.customerPart?.name || null,
      deliveredBy: values.deliveredBy.toUpperCase().replace(/ /g, "_") as "CSC_LAB" | "REQUESTER",
      description: values.description,
      externalId: values.externalId,
      id: substrateId || undefined,
      lotNumber: values.lotNumber,
      manufacturer: values.manufacturer,
      receivedOn: values.receivedOn ? values.receivedOn : null,
      sampleName: values.sampleName,
      source: values.source,
      substrateClassLayers: substrateObject
        ? substrateObject.substrateClassLayers
        : substrateClasses.map((selection) => ({
            substrateClassId: selection.substrateClass.tmbId,
            substrateClassName: selection.substrateClass.name,
            substrateClassType: selection.substrateClassType,
            productReferenceId: selection.kind === "tape" ? selection.substrateClass.productId : null,
            productReferenceName: selection.kind === "tape" ? selection.substrateClass.productName : null,
          })),
      substrateType: substrateContext[0] === "NON_STANDARD" ? "CUSTOMER_STANDARD" : substrateContext[0],
      usage: values.usage || "FIRST_TIME_USE",
      characteristics: values.characteristics,
    };

    const testRequestId = testRequest?.requestId;
    if (testRequestId && substrateId) {
      return substrateSamplesApi
        .updateSubstrateSampleInformation(testRequestId, substrateId, payload)
        .then((res) => res.data);
    }
    return substrateSamplesApi.createSubstrateSampleInformation(payload).then((resp) => {
      if (permissions?.editDefinition && testRequestId) {
        const newSubstrateId = Number(resp.data.id);
        return testRequestApi.addTestRequestSubstrateSample(testRequestId, newSubstrateId).then(() => {
          return {
            ...resp.data,
            attachments: [],
          };
        });
      }
      return resp.data;
    });
  };

  const update = (
    values: SubstrateSampleForm,
    actions: FormikHelpers<SubstrateSampleForm>,
    doSave: boolean = true,
  ): void => {
    if (disabled || !formRef?.dirty || !permissions?.editDefinition) {
      actions.resetForm();
      callback(true);
      return;
    }
    if (!doSave) {
      setIsCustomerPartDialogOpen(true);
      return;
    }
    // Confidentiality check logic
    const fieldsToCheck = [values.sampleName, values.manufacturer, values.description, values.source, values.lotNumber];
    if (confidentialRegex && containsHighlyConfidentialInfo(fieldsToCheck, confidentialRegex)) {
      setPendingValues(values);
      setShowConfidentialDialog(true);
      return;
    }
    save(values)
      .then(() => {
        actions.resetForm();
        callback(true);
        showSnackbar("Substrate sample updated", "success");
      })
      .catch(handleHttpError);
  };

  const showEditCharacteristics = !disabled && permissions?.editCharacteristics;
  const showGallery =
    open &&
    (substrateId || substrateId === 0) &&
    (requestIdOverride || testRequest?.requestId) &&
    !!substrateObject?.attachments;
  const showExtraSaveButton = !disabled && !showGallery && !substrateId && permissions?.editAttachments;
  const showGalleryAddButton = !disabled && showGallery && permissions?.editAttachments;
  const showEditCharacteristicsAndUploadsInfo = showEditCharacteristics || showGalleryAddButton || showExtraSaveButton;

  return (
    <>
      <CustomDialog fullWidth maxWidth="lg" onClose={onClose} open={open}>
        {substrateObject?.usedIn ? (
          <h2 className={classes.header}>
            <WithPredefinedChip
              details={{
                testRequestId: substrateObject.usedIn,
                sampleType: SampleType.Substrate,
              }}
              spacing={2}
              text={substrateSampleName.toUpperCase()}
            />
          </h2>
        ) : (
          <h2 className={classes.header}>{substrateSampleName.toUpperCase()}</h2>
        )}
        <Formik
          innerRef={(ref) => {
            if (ref) {
              formRef = ref;
            }
          }}
          initialValues={{
            customerPart: substrateObject?.customerPartTypeName
              ? {
                  id: substrateObject?.customerPartTypeId || 0,
                  name: substrateObject?.customerPartTypeName || "",
                }
              : null,
            deliveredBy: substrateObject?.deliveredBy || "",
            description: substrateObject?.description || "",
            externalId: substrateObject?.externalId || "",
            lotNumber: substrateObject?.lotNumber || "",
            manufacturer: substrateObject?.manufacturer || "",
            receivedOn: substrateObject?.receivedOn ? new Date(substrateObject?.receivedOn) : null,
            sampleName: substrateObject
              ? substrateObject.sampleName
              : substrateClasses[0]?.kind === "standard"
              ? substrateClasses[0]?.substrateClass.name
              : substrateClasses[0]?.substrateClass.productName || "",
            source: substrateObject?.source || "",
            substrateClass: substrateObject
              ? substrateObject.substrateClassLayers[0]?.substrateClassName
              : substrateClasses[0]?.substrateClass.name,
            substrateId: substrateObject ? substrateId : null,
            usage: substrateObject?.usage || "",
            composition:
              substrateObject?.substrateClassLayers.map(labelOfReference).join(" / ") ||
              substrateClasses.map(labelOfSubstrateClassSelection).join(" / "),
            characteristics: [...parameterValues],
          }}
          onSubmit={(values, actions) => {
            if (isCustomerPart) {
              update(values, actions, !!values.customerPart?.id);
            } else {
              update(values, actions, true);
            }
          }}
          validationSchema={yupValidationSchema}
        >
          {(formikProps: FormikProps<SubstrateSampleForm>): JSX.Element => {
            const { errors, handleBlur, handleChange, setFieldValue, touched, values } = formikProps;
            return (
              <Form>
                <Box pb={4}>
                  <h3>Substrate Sample Definition</h3>
                  <Grid container justify="flex-start" direction="row" spacing={4}>
                    <Grid item lg={6} md={6} sm={6} xs={6}>
                      <InfoBox text={INFO_SAMPLE_NAME}>
                        <CustomTextField
                          autoFocus
                          mandatory
                          fullWidth
                          disabled={formFieldsDisabled}
                          name="sampleName"
                          id="sampleName"
                          label="Sample Name"
                          value={values.sampleName}
                          type="sampleName"
                          helperText={[errors.sampleName && touched.sampleName ? errors.sampleName : ""].join(" ")}
                          error={!!(errors.sampleName && touched.sampleName)}
                          onChange={(event): void => {
                            const changedValue = event.target.value;
                            setSubstrateSampleName(changedValue);
                            handleChange(event);
                          }}
                          onBlur={handleBlur}
                        />
                      </InfoBox>
                    </Grid>
                    <Grid item lg={3} md={3} sm={3} xs={3}>
                      <CustomTextField
                        fullWidth
                        disabled
                        name="substrateClass"
                        id="substrateClass"
                        label="Substrate Class"
                        value={values.substrateClass}
                        type="substrateClass"
                        helperText={[errors.substrateClass && touched.substrateClass ? errors.substrateClass : ""].join(
                          " ",
                        )}
                        error={!!(errors.substrateClass && touched.substrateClass)}
                        onChange={handleChange}
                        onBlur={handleBlur}
                      />
                    </Grid>
                    {substrateObject ? (
                      <Grid item lg={3} md={3} sm={3} xs={3}>
                        <CustomTextField
                          fullWidth
                          disabled
                          name="substrateId"
                          id="substrateId"
                          label="Substrate Sample ID"
                          value={values.substrateId}
                          type="substrateId"
                          helperText={[errors.substrateId && touched.substrateId ? errors.substrateId : ""].join(" ")}
                          error={!!(errors.substrateId && touched.substrateId)}
                          onChange={handleChange}
                          onBlur={handleBlur}
                        />
                      </Grid>
                    ) : (
                      <Grid item lg={3} md={3} sm={3} xs={3} />
                    )}
                    <Grid
                      item
                      lg={isCustomerPart ? 6 : 12}
                      md={isCustomerPart ? 6 : 12}
                      sm={isCustomerPart ? 6 : 12}
                      xs={isCustomerPart ? 6 : 12}
                    >
                      <CustomTextField fullWidth disabled label="Composition" value={values.composition} />
                    </Grid>
                    {isCustomerPart && (
                      <Grid item lg={6} md={6} sm={6} xs={6}>
                        <CustomerPartSearchAutocomplete
                          freeSolo
                          disabled={formFieldsDisabled}
                          error={!!(errors.customerPart && touched.customerPart)}
                          helperText={[errors.customerPart && touched.customerPart ? errors.customerPart : ""].join(
                            " ",
                          )}
                          inputLabel="Type of Customer Part"
                          mandatory
                          optionFetch={(term): Promise<PagedResponse<CustomerPart>> =>
                            tmbApi.fetchCustomerPartTypes(term).then((res) => res.data)
                          }
                          onChange={(e, value): void => {
                            setFieldValue("customerPart", value);
                          }}
                          map={(value): { key: string; label: string } => ({
                            key: `${value.id || value.name}`,
                            label: value.name,
                          })}
                          mapToData={(name) => ({
                            id: null,
                            name,
                          })}
                          value={values.customerPart}
                        />
                      </Grid>
                    )}
                    <Grid item lg={3} md={3} sm={3} xs={3}>
                      <InfoBox text={INFO_DELIVERED_BY}>
                        <CustomRadioGroup
                          disabled={formFieldsDisabled}
                          defaultValue={substrateObject ? substrateObject.deliveredBy : ""}
                          error={!!(errors.deliveredBy && touched.deliveredBy)}
                          formLabel="Delivered By*"
                          name="deliveredBy"
                          options={["CSC Lab", "Requester"]}
                          onChange={handleChange}
                          value={values.deliveredBy}
                        />
                      </InfoBox>
                    </Grid>
                    <Grid item lg={3} md={3} sm={3} xs={3}>
                      <InfoBox text={INFO_SOURCE}>
                        <CustomTextField
                          fullWidth
                          disabled={formFieldsDisabled}
                          name="source"
                          id="source"
                          label="Source"
                          value={values.source}
                          type="source"
                          helperText={[errors.source && touched.source ? errors.source : ""].join(" ")}
                          error={!!(errors.source && touched.source)}
                          onChange={handleChange}
                          onBlur={handleBlur}
                        />
                      </InfoBox>
                    </Grid>
                    <Grid item lg={3} md={3} sm={3} xs={3}>
                      <CustomDatePicker
                        disabled={formFieldsDisabled}
                        label="Received On"
                        value={values.receivedOn}
                        onChange={(date: MaterialUiPickersDate) => {
                          setFieldValue("receivedOn", date);
                        }}
                      />
                    </Grid>
                    <Grid item lg={3} md={3} sm={3} xs={3}>
                      <CustomRadioGroup
                        disabled={formFieldsDisabled}
                        defaultValue={substrateObject ? substrateObject.usage : ""}
                        error={!!(errors.usage && touched.usage)}
                        formLabel="Condition of Sample*"
                        name="usage"
                        options={["First Time Use", "Reuse"]}
                        onChange={handleChange}
                        value={values.usage}
                      />
                    </Grid>
                    <Grid item lg={6} md={6} sm={6} xs={6}>
                      <CustomTextField
                        fullWidth
                        disabled={formFieldsDisabled}
                        name="manufacturer"
                        id="manufacturer"
                        label="Manufacturer"
                        value={values.manufacturer}
                        type="manufacturer"
                        helperText={[errors.manufacturer && touched.manufacturer ? errors.manufacturer : ""].join(" ")}
                        error={!!(errors.manufacturer && touched.manufacturer)}
                        onChange={handleChange}
                        onBlur={handleBlur}
                      />
                    </Grid>
                    <Grid item lg={3} md={3} sm={3} xs={3}>
                      <CustomTextField
                        fullWidth
                        disabled={formFieldsDisabled}
                        name="externalId"
                        id="externalId"
                        label="External ID"
                        value={values.externalId}
                        type="externalId"
                        helperText={[errors.externalId && touched.externalId ? errors.externalId : ""].join(" ")}
                        error={!!(errors.externalId && touched.externalId)}
                        onChange={handleChange}
                        onBlur={handleBlur}
                      />
                    </Grid>
                    <Grid item lg={3} md={3} sm={3} xs={3}>
                      <CustomTextField
                        fullWidth
                        disabled={formFieldsDisabled}
                        name="lotNumber"
                        id="lotNumber"
                        label="Lot Number"
                        value={values.lotNumber}
                        type="lotNumber"
                        helperText={[errors.lotNumber && touched.lotNumber ? errors.lotNumber : ""].join(" ")}
                        error={!!(errors.lotNumber && touched.lotNumber)}
                        onChange={handleChange}
                        onBlur={handleBlur}
                      />
                    </Grid>
                    <Grid item lg={12} md={12} sm={12} xs={12}>
                      <InfoBox text={INFO_DESCRIPTION}>
                        <CustomTextField
                          fullWidth
                          disabled={formFieldsDisabled}
                          name="description"
                          id="description"
                          label="Notes and Remarks"
                          value={values.description}
                          type="description"
                          helperText={[errors.description && touched.description ? errors.description : ""].join(" ")}
                          error={!!(errors.description && touched.description)}
                          onChange={handleChange}
                          onBlur={handleBlur}
                        />
                      </InfoBox>
                    </Grid>
                  </Grid>
                </Box>
                <Box pb={4}>
                  {parameterValues.length > 0 && (
                    <>
                      <InfoBox compress text={INFO_CHARACTERISTICS}>
                        <h3>Substrate Sample Characteristics</h3>
                      </InfoBox>
                      <SubstrateCharacteristicsTable
                        disabled={disabled || !permissions?.editCharacteristics}
                        parameterValues={parameterValues}
                        onEdit={(value): void => {
                          showParameterListDialog(value.parameter.name, [value.parameter], [value])
                            .then((newValues) => {
                              parameterValues.splice(parameterValues.indexOf(value), 1, newValues[0]);
                              setParameterValues([...parameterValues]);
                              formRef?.setFieldValue("characteristics", [...parameterValues]);
                            })
                            .catch(() => {
                              // do nothing
                            });
                        }}
                        onRemove={(value): void => {
                          parameterValues.splice(parameterValues.indexOf(value), 1);
                          setParameterValues([...parameterValues]);
                          formRef?.setFieldValue("characteristics", [...parameterValues]);
                        }}
                      />
                    </>
                  )}
                  <InfoBox
                    compress
                    text={INFO_EDIT_CHARACTERISTICS_GALLERY}
                    hidden={!showEditCharacteristicsAndUploadsInfo}
                  >
                    <Box display="flex" flexDirection="row">
                      {showEditCharacteristics && (
                        <Box marginRight={2}>
                          <CustomDynamicContextMenu<TmbParameter>
                            sortAlphanumericBy="name"
                            fetch={(): Promise<TmbParameter[]> => {
                              return tmbApi.fetchSubstrateParameters().then((response) => response.data.data);
                            }}
                            onMenuItemClick={(selectable): void => {
                              showParameterListDialog(selectable.name, [mapParameter(selectable)], [])
                                .then((newValues) => {
                                  const characteristics = [...parameterValues];
                                  characteristics.push(newValues[0]);
                                  characteristics.sort(sortParameterValue);
                                  setParameterValues(characteristics);
                                  formRef?.setFieldValue("characteristics", characteristics);
                                })
                                .catch(() => {
                                  // do nothing
                                });
                            }}
                            buttonContent={
                              <CustomButton
                                startIcon={
                                  <Box width={22} paddingTop={0.5} marginRight={1}>
                                    <PlaylistAdd fontSize="small" />
                                  </Box>
                                }
                                text="Add Characteristic"
                              />
                            }
                          />
                        </Box>
                      )}
                      {showGalleryAddButton && (
                        <Box marginRight={2}>
                          <GalleryUploadButton hideIfNonEmpty />
                        </Box>
                      )}
                      {showExtraSaveButton && (
                        <GalleryButton
                          icon="UPLOAD"
                          text="Add Attachment"
                          onClick={() =>
                            showConfirmationDialog({
                              headline: "Save Substrate Sample",
                              text: "You have to save the Substrate Sample to add attachments.",
                              confirmText: "Save",
                            })
                              .then(() => {
                                return formRef?.validateForm();
                              })
                              .then((formErrors) => {
                                if (formErrors && Object.keys(formErrors).length > 0) {
                                  formRef?.submitForm();
                                  throw new HiddenError("form validation failed");
                                }
                                return formRef?.values;
                              })
                              .then((formValues) => {
                                if (formValues) {
                                  return save(formValues).then((response) => {
                                    callback(false);
                                    formRef?.setFieldValue("substrateId", response.id || null);
                                    updateSubstrateId(response.id || null);
                                    updateSubstrateObject(response);
                                    showSnackbar("Saved successfully.", "success");
                                  });
                                }
                              })
                              .catch(handleHttpError)
                          }
                        />
                      )}
                    </Box>
                  </InfoBox>
                </Box>
                {showGallery && (
                  <SubstrateSampleGallery
                    editable={permissions?.editAttachments || false}
                    testRequestId={requestIdOverride || (testRequest?.requestId as string)}
                    substrateSampleId={substrateId as number}
                    substrateSampleName={substrateSampleName}
                    substrateAttachments={substrateObject?.attachments as SubstrateAttachment[]}
                    onChange={(attachments) => {
                      const existingAttachments = substrateObject?.attachments;
                      if (existingAttachments) {
                        existingAttachments.splice(0, existingAttachments.length, ...attachments);
                      }
                    }}
                  />
                )}
                <Grid container direction="row" justify="flex-end">
                  <CustomButton type="submit" primary text={formFieldsDisabled ? "Close" : "Save and Close"} />
                </Grid>
                <CustomerPartCreateDialog
                  initialValue={values.customerPart}
                  onClose={() => setIsCustomerPartDialogOpen(false)}
                  onConfirm={(customerPart) => {
                    update({ ...values, customerPart }, formikProps, true);
                    setIsCustomerPartDialogOpen(false);
                  }}
                  open={isCustomerPartDialogOpen}
                />
              </Form>
            );
          }}
        </Formik>
      </CustomDialog>
      {/* Confidential confirmation dialog */}
      {showConfidentialDialog && (
        <ConfidentialConfirmationDialog
          objectName="substrate sample"
          open={showConfidentialDialog}
          onCancel={() => {
            setShowConfidentialDialog(false);
            setPendingValues(null);
          }}
          onProceed={() => {
            if (pendingValues) {
              save(pendingValues)
                .then(() => {
                  formRef?.resetForm();
                  callback(true);
                  showSnackbar("Substrate sample updated", "success");
                })
                .catch(handleHttpError);
            }
            setShowConfidentialDialog(false);
            setPendingValues(null);
          }}
        />
      )}
    </>
  );
};

export default SubstrateDetailsDialog;
